import React from 'react';
import { Clock, AlertCircle, Shield, MessageSquare, TrendingDown, Zap } from 'lucide-react';
import { Pattern } from '../../types/scan';
interface ShareableResultCardProps {
  score: number;
  riskLevel: 'low' | 'medium' | 'high';
  messageCount?: number;
  topPatterns?: Pattern[];
  variant?: 'square' | 'vertical';
  summary?: string;
  theme?: 'dark' | 'light';
}
export function ShareableResultCard({
  score,
  riskLevel = 'low',
  messageCount = 0,
  topPatterns = [],
  variant = 'square',
  summary,
  theme: cardTheme = 'dark'
}: ShareableResultCardProps) {
  const isVertical = variant === 'vertical';
  const isLight = cardTheme === 'light';
  // Theme Configuration based on risk level
  const themeConfig = {
    high: {
      color: '#EF4444',
      glowColor: 'rgba(239, 68, 68, 0.5)',
      textClass: 'text-red-500',
      bgClass: 'bg-red-500/10',
      borderClass: 'border-red-500/20',
      label: 'High Risk',
      verdict: 'TOXIC',
      summary: 'This conversation shows highly toxic communication patterns.',
      lightBadgeBg: '#FEF2F2',
      lightBadgeText: '#DC2626',
      lightBadgeBorder: '#FCA5A5'
    },
    medium: {
      color: '#F59E0B',
      glowColor: 'rgba(245, 158, 11, 0.5)',
      textClass: 'text-amber-500',
      bgClass: 'bg-amber-500/10',
      borderClass: 'border-amber-500/20',
      label: 'Medium Risk',
      verdict: 'CONCERNING',
      summary: 'This conversation shows some concerning communication patterns.',
      lightBadgeBg: '#FFFBEB',
      lightBadgeText: '#D97706',
      lightBadgeBorder: '#FCD34D'
    },
    low: {
      color: '#10B981',
      glowColor: 'rgba(16, 185, 129, 0.5)',
      textClass: 'text-emerald-500',
      bgClass: 'bg-emerald-500/10',
      borderClass: 'border-emerald-500/20',
      label: 'Low Risk',
      verdict: 'HEALTHY',
      summary: 'This conversation shows generally healthy communication patterns.',
      lightBadgeBg: '#ECFDF5',
      lightBadgeText: '#059669',
      lightBadgeBorder: '#6EE7B7'
    }
  };
  const theme = themeConfig[riskLevel] || themeConfig.low;
  // Helper to extract pattern info
  const getPatternInfo = (pattern: any) => {
    if (typeof pattern === 'string') {
      return {
        type: pattern,
        severity: 'MEDIUM' as const,
        description: ''
      };
    }
    return {
      type: pattern.type || 'Unknown',
      severity: pattern.severity || 'MEDIUM',
      description: pattern.description || ''
    };
  };
  // Helper functions
  const getPatternIcon = (type: string) => {
    if (!type) return Clock;
    const lower = type.toLowerCase();
    if (lower.includes('gaslight')) return AlertCircle;
    if (lower.includes('manipulat')) return Shield;
    if (lower.includes('dismiss')) return MessageSquare;
    if (lower.includes('blame')) return TrendingDown;
    if (lower.includes('aggress')) return Zap;
    return Clock;
  };
  const getSeverityColor = (severity: string) => {
    const s = severity?.toLowerCase();
    if (s === 'high') return '#EF4444';
    if (s === 'medium') return '#F59E0B';
    return '#10B981';
  };
  const getLightSeverityStyles = (severity: string) => {
    const s = severity?.toLowerCase();
    if (s === 'high') return {
      bg: '#FEF2F2',
      text: '#DC2626',
      border: '#FCA5A5'
    };
    if (s === 'medium') return {
      bg: '#FFFBEB',
      text: '#D97706',
      border: '#FCD34D'
    };
    return {
      bg: '#ECFDF5',
      text: '#059669',
      border: '#6EE7B7'
    };
  };
  const displayPatterns = topPatterns.slice(0, isVertical ? 4 : 3);
  const today = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const displaySummary = summary || theme.summary;
  // Common Background Grid
  const BackgroundGrid = () => <div className="absolute inset-0 pointer-events-none opacity-20" data-id="element-129" data-mp="3d0f315zdfrp6">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)]" data-id="element-130" data-mp="3812dgdh486iw" />
    </div>;
  if (isVertical) {
    // --- VERTICAL (STORY) VARIANT ---
    const radius = 160;
    const strokeWidth = 28;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - score / 100 * circumference;
    const ringSize = 400;
    if (isLight) {
      return <div className="w-[1080px] h-[1920px] bg-[#FAFAFA] flex flex-col font-sans text-slate-900 relative overflow-hidden" data-id="element-131" data-mp="3d5e8j4mnm6qo">
          {/* Header */}
          <header className="px-16 pt-20 pb-8 relative z-10" data-id="element-132" data-mp="3i2wbmedzprqr">
            <div className="flex items-center justify-between mb-6" data-id="element-133" data-mp="3n43kkgrvcwz0">
              <h1 className="text-3xl font-black tracking-tight text-slate-900" data-id="element-134" data-mp="3n17neunuicbm">
                ToxicDetector.ai
              </h1>
              <span className="text-xl text-slate-500 font-medium" data-id="element-135" data-mp="12cjvpkhfn3h0">
                Chat Analysis Result
              </span>
            </div>
            <div className="w-full h-px bg-slate-200" data-id="element-136" data-mp="2mulyvcbid83a" />
          </header>

          {/* Main Content */}
          <main className="flex-1 flex flex-col items-center px-16 pt-12 relative z-10" data-id="element-137" data-mp="2hydr4jzn4h65">
            {/* Score Ring */}
            <div className="relative flex items-center justify-center mb-12" style={{
            width: ringSize,
            height: ringSize
          }} data-id="element-138" data-mp="286hgk9gdayl1">
              <svg className="w-full h-full transform -rotate-90" viewBox={`0 0 ${ringSize} ${ringSize}`} data-id="element-139" data-mp="1z62qyxwdexbq">
                <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={strokeWidth} data-id="element-140" data-mp="1u6ylah8nnhq5" />
                <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={theme.color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} data-id="element-141" data-mp="3jl4t8ykfjf56" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-142" data-mp="1cyfhh1hu1o9t">
                <span className="text-[130px] font-black tracking-tighter leading-none text-slate-900" data-id="element-143" data-mp="20movhh8t17qe">
                  {score}%
                </span>
              </div>
            </div>

            {/* Verdict */}
            <div className="flex flex-col items-center mb-12" data-id="element-144" data-mp="1rlihpfxjib0u">
              <h2 className="text-7xl font-black tracking-tight mb-6 uppercase text-center text-slate-900" data-id="element-145" data-mp="1vncbr5d8cgs1">
                {score}% {theme.verdict}
              </h2>
              <div className="px-6 py-2 rounded-full border flex items-center gap-2" style={{
              backgroundColor: theme.lightBadgeBg,
              borderColor: theme.lightBadgeBorder
            }} data-id="element-146" data-mp="g9hn3kdzvsd6">
                <div className="w-2.5 h-2.5 rounded-full" style={{
                backgroundColor: theme.color
              }} data-id="element-147" data-mp="7pkysjgvzbxs" />
                <span className="text-xl font-bold uppercase tracking-wide" style={{
                color: theme.lightBadgeText
              }} data-id="element-148" data-mp="hplkud65465o">
                  {theme.label} Detected
                </span>
              </div>
            </div>

            {/* Summary */}
            <div className="text-center mb-4 max-w-3xl" data-id="element-149" data-mp="3qc51eluje28">
              <p className="text-3xl font-semibold text-slate-800 leading-relaxed" data-id="element-150" data-mp="3v2f16l6q9o5e">
                {displaySummary}
              </p>
            </div>

            <div className="text-center mb-16" data-id="element-151" data-mp="32erijbp0p7le">
              <p className="text-xl text-slate-500 font-medium" data-id="element-152" data-mp="32dzz0z8p30bh">
                Based on communication patterns, not intent.
              </p>
            </div>

            {/* Patterns List */}
            <div className="w-full max-w-4xl flex flex-col gap-6 mb-16" data-id="element-153" data-mp="2lv2q4ibtboim">
              {displayPatterns.map((pattern, index) => {
              const info = getPatternInfo(pattern);
              const PatternIcon = getPatternIcon(info.type);
              const lightStyles = getLightSeverityStyles(info.severity);
              return <div key={index} className="bg-white rounded-3xl border border-slate-200 p-8 flex items-center justify-between shadow-sm" data-id="element-154" data-mp="34t5f4x4d1lgf" data-mpi={index}>
                    <div className="flex items-center gap-5" data-id="element-155" data-mp="3eip1wdv71zgk">
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-600" data-id="element-156" data-mp="3ei5h8pe5swyu">
                        <PatternIcon size={24} strokeWidth={2} data-id="element-157" data-mp="3njmvs3bx4qlf" />
                      </div>
                      <h3 className="text-3xl font-bold text-slate-900 capitalize" data-id="element-158" data-mp="3nhag5wtmusbj">
                        {info.type}
                      </h3>
                    </div>
                    <span className="px-5 py-2 rounded-full text-sm font-bold uppercase tracking-widest border" style={{
                  backgroundColor: lightStyles.bg,
                  color: lightStyles.text,
                  borderColor: lightStyles.border
                }} data-id="element-159" data-mp="16vncqnybcrsy">
                      {info.severity}
                    </span>
                  </div>;
            })}

              {displayPatterns.length === 0 && <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center shadow-sm" data-id="element-160" data-mp="1gvmi6bzfs21l">
                  <p className="text-2xl font-medium text-slate-500" data-id="element-161" data-mp="2eba7dwrq29r5">
                    No concerning patterns detected.
                  </p>
                </div>}
            </div>

            {/* Stats Row */}
            <div className="flex items-center justify-center gap-12 text-2xl font-bold text-slate-900 mb-12" data-id="element-162" data-mp="2dpr0hm8r2hpc">
              <div className="flex flex-col items-start" data-id="element-163" data-mp="28rfszi5xgjaa">
                <span className="text-lg font-bold text-slate-900 mb-1" data-id="element-164" data-mp="2mq5q2xsqyiyn">
                  Messages:
                </span>
                <span className="text-xl" data-id="element-165" data-mp="2imzow4khrhz1">{messageCount}</span>
              </div>
              <div className="w-px h-12 bg-slate-200" data-id="element-166" data-mp="2wn20j927hf5u" />
              <div className="flex flex-col items-start" data-id="element-167" data-mp="2wlqz3fw8gjhw">
                <span className="text-lg font-bold text-slate-900 mb-1" data-id="element-168" data-mp="2rnpbchnv3w03">
                  Patterns Found: {topPatterns.length}
                </span>
              </div>
              <div className="w-px h-12 bg-slate-200" data-id="element-169" data-mp="31e0i0byxkjao" />
              <div className="flex flex-col items-start" data-id="element-170" data-mp="31fbv52e9n3eg">
                <span className="text-lg font-bold text-slate-900 mb-1" data-id="element-171" data-mp="3fdjshbf7s6q5">
                  Risk Level:
                </span>
                <span className="text-xl capitalize" data-id="element-172" data-mp="3agtceo3etb4q">{riskLevel}</span>
              </div>
            </div>

            <div className="text-center mb-auto" data-id="element-173" data-mp="3u1th2nd0han0">
              <p className="text-2xl font-medium text-slate-700" data-id="element-174" data-mp="2v8ubftzoovs">
                Scan your conversations for unhealthy patterns
              </p>
            </div>

            {/* Brand Footer */}
            <div className="w-full max-w-4xl bg-[#F8FAFC] rounded-3xl p-10 flex flex-col items-center justify-center text-center mt-12 mb-16" data-id="element-175" data-mp="3p1e5a08dob75">
              <div className="flex items-center gap-4 mb-4" data-id="element-176" data-mp="3b8xweisspiyb">
                <span className="text-4xl" data-id="element-177" data-mp="3g1ipfi3vns0s">🚩</span>
                <span className="text-4xl font-black text-slate-900 tracking-tight" data-id="element-178" data-mp="3g3bzz8hm2we2">
                  Made with ToxicDetector.ai
                </span>
              </div>
              <span className="text-2xl text-slate-500 font-medium" data-id="element-179" data-mp="36c7h3xoterdy">
                AI-powered relationship analysis
              </span>
            </div>
          </main>
        </div>;
    }
    return <div className="w-[1080px] h-[1920px] bg-[#0A0A0A] flex flex-col font-sans text-white relative overflow-hidden" style={{
      '--theme-glow-color': theme.glowColor
    } as React.CSSProperties} data-id="element-180" data-mp="2xa7yj0y5jf8w">
        <BackgroundGrid data-id="element-181" data-mp="t47vroeviv92" />

        {/* Subtle glow behind ring */}
        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 pointer-events-none" style={{
        backgroundColor: theme.color
      }} data-id="element-182" data-mp="t2daua3lr34z" />

        {/* Header */}
        <header className="px-16 pt-20 pb-8 relative z-10" data-id="element-183" data-mp="1gutweumjtd91">
          <div className="flex items-center justify-between mb-6" data-id="element-184" data-mp="1cs79970togi6">
            <div className="flex items-center gap-4" data-id="element-185" data-mp="1cujrzmeemuwa">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-black font-black text-xl tracking-tighter" data-id="element-186" data-mp="17sud1vo29x2x">
                TD
              </div>
              <h1 className="text-3xl font-black tracking-tight text-white" data-id="element-187" data-mp="3sjle99b8drsn">
                ToxicDetector.ai
              </h1>
            </div>
            <span className="text-xl text-white/50 font-mono uppercase tracking-widest" data-id="element-188" data-mp="283n8dgkjtnxo">
              Forensic Report
            </span>
          </div>
          <div className="w-full h-px bg-white/10" data-id="element-189" data-mp="2h28pd7adguwc" />
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center px-16 pt-12 relative z-10" data-id="element-190" data-mp="2ltgzy6nfumb3">
          {/* Score Ring */}
          <div className="relative flex items-center justify-center mb-12" style={{
          width: ringSize,
          height: ringSize
        }} data-id="element-191" data-mp="2qrii8pd1ihli">
            <svg className="w-full h-full transform -rotate-90" viewBox={`0 0 ${ringSize} ${ringSize}`} style={{
            filter: 'drop-shadow(0 0 20px var(--theme-glow-color))'
          }} data-id="element-192" data-mp="30rsx0eeq3x2s">
              <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={strokeWidth} data-id="element-193" data-mp="1adsc7d2d1j98" />
              <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={theme.color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} data-id="element-194" data-mp="b1qqtxz5gzqe" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-195" data-mp="3nl9vj168uqry">
              <span className="text-[130px] font-black tracking-tighter leading-none" style={{
              color: theme.color
            }} data-id="element-196" data-mp="63z21ctsxtqf">
                {score}
              </span>
              <span className="text-2xl font-mono text-white/50 mt-2 uppercase tracking-widest" data-id="element-197" data-mp="39pxqo0ydjjxb">
                Score
              </span>
            </div>
          </div>

          {/* Verdict */}
          <div className="flex flex-col items-center mb-16" data-id="element-198" data-mp="1k9bw3t6xrbba">
            <h2 className="text-7xl font-black tracking-tight mb-4 uppercase text-center" style={{
            color: theme.color
          }} data-id="element-199" data-mp="1p3g5ej3obvfj">
              {theme.verdict}
            </h2>
            <div className={`px-6 py-2 rounded-full border ${theme.borderClass} ${theme.bgClass}`} data-id="element-200" data-mp="231mv3bygyp44">
              <span className={`text-xl font-bold ${theme.textClass} uppercase tracking-widest`} data-id="element-201" data-mp="280zhxwpcbw6p">
                {theme.label} Detected
              </span>
            </div>
          </div>

          {/* Summary */}
          <div className="text-center mb-16 max-w-3xl" data-id="element-202" data-mp="1z25p8hf4rpyf">
            <p className="text-3xl font-medium text-white/90 leading-relaxed" data-id="element-203" data-mp="ilji57xb3mth">
              {displaySummary}
            </p>
          </div>

          {/* Patterns List */}
          <div className="w-full max-w-4xl flex flex-col gap-6 mb-16" data-id="element-204" data-mp="2p9sc5154dyt9">
            <h3 className="text-2xl font-mono text-white/50 uppercase tracking-widest mb-2 pl-4" data-id="element-205" data-mp="2pc61quh16itf">
              Detected Patterns
            </h3>
            {displayPatterns.map((pattern, index) => {
            const info = getPatternInfo(pattern);
            const PatternIcon = getPatternIcon(info.type);
            const sevColor = getSeverityColor(info.severity);
            return <div key={index} className="bg-white/5 rounded-3xl border border-white/10 p-8 flex flex-col gap-4 relative overflow-hidden" data-id="element-206" data-mp="26h7kto52k1rk" data-mpi={index}>
                  {/* Severity Accent Line */}
                  <div className="absolute left-0 top-0 bottom-0 w-2" style={{
                backgroundColor: sevColor
              }} data-id="element-207" data-mp="1skko9mh13dm6" />

                  <div className="flex items-center justify-between pl-4" data-id="element-208" data-mp="3myvdvx5g6bt0">
                    <div className="flex items-center gap-5" data-id="element-209" data-mp="18qkp5mbxc0mz">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white" data-id="element-210" data-mp="18nyidowuxp5t">
                        <PatternIcon size={24} strokeWidth={2} data-id="element-211" data-mp="1ifukrc5z81fu" />
                      </div>
                      <h3 className="text-3xl font-bold text-white capitalize" data-id="element-212" data-mp="1mhg1x9pjn1vu">
                        {info.type}
                      </h3>
                    </div>
                    <span className="px-5 py-2 rounded-full text-sm font-mono font-bold uppercase tracking-widest" style={{
                  '--sev-color': sevColor,
                  '--sev-color-20': `${sevColor}20`,
                  '--sev-color-40': `${sevColor}40`,
                  backgroundColor: 'var(--sev-color-20)',
                  color: 'var(--sev-color)',
                  border: '1px solid var(--sev-color-40)'
                } as React.CSSProperties} data-id="element-213" data-mp="pt1deay303wv">
                      {info.severity}
                    </span>
                  </div>
                  {info.description && <p className="text-xl text-white/60 leading-relaxed pl-21 ml-[68px]" data-id="element-214" data-mp="1zmwmc4hsnm2a">
                      {info.description}
                    </p>}
                </div>;
          })}

            {displayPatterns.length === 0 && <div className="bg-white/5 rounded-3xl border border-white/10 p-12 text-center" data-id="element-215" data-mp="24lhedgmw6zfn">
                <p className="text-2xl font-medium text-white/50" data-id="element-216" data-mp="2il9063jnhk47">
                  No concerning patterns detected.
                </p>
              </div>}
          </div>

          {/* Stats Row */}
          <div className="flex items-center justify-center gap-12 text-2xl font-mono text-white/60 mb-auto" data-id="element-217" data-mp="o5y2gr9fbkwf">
            <div className="flex flex-col items-center" data-id="element-218" data-mp="k6604wamiazi">
              <span className="text-white font-bold text-4xl mb-2" data-id="element-219" data-mp="a161yl3l03h5">
                {messageCount}
              </span>
              <span className="text-sm uppercase tracking-widest" data-id="element-220" data-mp="e3atneagyq15">
                Messages
              </span>
            </div>
            <div className="w-px h-16 bg-white/10" data-id="element-221" data-mp="al81ayurn4k" />
            <div className="flex flex-col items-center" data-id="element-222" data-mp="5a7l9keol3dd">
              <span className="text-white font-bold text-4xl mb-2" data-id="element-223" data-mp="58mpxskjlgeg">
                {topPatterns.length}
              </span>
              <span className="text-sm uppercase tracking-widest" data-id="element-224" data-mp="3mkxirdyz4vsu">
                Patterns
              </span>
            </div>
          </div>

          {/* Brand Footer */}
          <div className="w-full max-w-4xl bg-white/5 border border-white/10 rounded-3xl p-10 flex flex-col items-center justify-center text-center mt-16 mb-16 relative overflow-hidden" data-id="element-225" data-mp="3djqxyspakyts">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50" data-id="element-226" data-mp="1br2rmahcpv9s" />
            <div className="flex items-center gap-4 mb-4" data-id="element-227" data-mp="2k9spj48vl3a9">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-black font-black text-lg tracking-tighter" data-id="element-228" data-mp="2k87uzemk9kce">
                TD
              </div>
              <span className="text-3xl font-black text-white tracking-tight" data-id="element-229" data-mp="2acnyor434jn4">
                ToxicDetector.ai
              </span>
            </div>
            <span className="text-xl text-white/50 font-mono uppercase tracking-widest" data-id="element-230" data-mp="2f8d255j86kop">
              AI-Powered Relationship Analysis
            </span>
          </div>
        </main>
      </div>;
  }
  // --- SQUARE (1:1) VARIANT ---
  if (isLight) {
    const radius = 120;
    const strokeWidth = 20;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - score / 100 * circumference;
    const ringSize = 300;
    return <div className="w-[1080px] h-[1080px] bg-[#FAFAFA] flex flex-col font-sans text-slate-900 relative overflow-hidden p-16" data-id="element-231" data-mp="1iivpg6ltrydi">
        {/* Header */}
        <header className="flex items-center justify-between mb-12 relative z-10" data-id="element-232" data-mp="1f29dctsrect1">
          <h1 className="text-4xl font-black tracking-tight text-slate-900" data-id="element-233" data-mp="1a3g8tqvg9w50">
            ToxicDetector.ai
          </h1>
          <div className="text-right" data-id="element-234" data-mp="1p18nkgt6agi5">
            <div className="text-slate-500 font-medium text-xl" data-id="element-235" data-mp="1p06yv9j8hvhy">
              Chat Analysis Result
            </div>
            <div className="text-slate-400 font-medium text-base mt-1" data-id="element-236" data-mp="1jzj8mq2txios">
              {today}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col relative z-10" data-id="element-237" data-mp="re9jvm42c8ar">
          <div className="flex items-center justify-between mb-16" data-id="element-238" data-mp="162ctd5hbe24b">
            <div className="flex flex-col" data-id="element-239" data-mp="163nwr46ul7uw">
              <h2 className="text-3xl font-medium text-slate-500 mb-4" data-id="element-240" data-mp="11aupo9nwpnfn">
                Analysis Result
              </h2>
              <span className="text-7xl font-black tracking-tight uppercase text-slate-900" data-id="element-241" data-mp="djz4hvdyhqhd">
                {score}% {theme.verdict}
              </span>
              <div className="mt-6 flex items-center gap-4" data-id="element-242" data-mp="yaujoi763asj">
                <div className="px-6 py-2 rounded-full border flex items-center gap-2" style={{
                backgroundColor: theme.lightBadgeBg,
                borderColor: theme.lightBadgeBorder
              }} data-id="element-243" data-mp="yacjjahdwcfc">
                  <div className="w-2.5 h-2.5 rounded-full" style={{
                  backgroundColor: theme.color
                }} data-id="element-244" data-mp="1c1hngorsbqvh" />
                  <span className="text-xl font-bold uppercase tracking-wide" style={{
                  color: theme.lightBadgeText
                }} data-id="element-245" data-mp="jdlrjma1i2zr">
                    {theme.label}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end" data-id="element-246" data-mp="24qeqxlrc7j46">
              <div className="relative flex items-center justify-center" style={{
              width: ringSize,
              height: ringSize
            }} data-id="element-247" data-mp="24p3obp9mohen">
                <svg className="w-full h-full transform -rotate-90" viewBox={`0 0 ${ringSize} ${ringSize}`} data-id="element-248" data-mp="3kbpc7qq4zcpu">
                  <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={strokeWidth} data-id="element-249" data-mp="3aj1ix5yd7qvv" />
                  <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={theme.color} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} data-id="element-250" data-mp="2wjibkfddojkm" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-251" data-mp="xut7reyaz48z">
                  <span className="text-[80px] font-black tracking-tighter leading-none text-slate-900" data-id="element-252" data-mp="12sur80zn55kz">
                    {score}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Patterns Box */}
          <div className="w-full flex-1 flex flex-col" data-id="element-253" data-mp="f38rncfcwocg">
            <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-254" data-mp="f1pbdltpadao">
              Detected Patterns ({topPatterns.length})
            </h3>

            <div className="grid grid-cols-1 gap-4" data-id="element-255" data-mp="564f84bbc78u">
              {displayPatterns.map((pattern, index) => {
              const info = getPatternInfo(pattern);
              const lightStyles = getLightSeverityStyles(info.severity);
              return <div key={index} className="flex items-center justify-between bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-id="element-256" data-mp="3scodszhfp3ft" data-mpi={index}>
                    <span className="text-3xl font-bold text-slate-900 capitalize pl-4" data-id="element-257" data-mp="3dpg7abz2lma6">
                      {info.type}
                    </span>
                    <span className="px-5 py-2 rounded-full text-lg font-bold uppercase tracking-widest border" style={{
                  backgroundColor: lightStyles.bg,
                  color: lightStyles.text,
                  borderColor: lightStyles.border
                }} data-id="element-258" data-mp="3mpcoxtn1lkvq">
                      {info.severity}
                    </span>
                  </div>;
            })}

              {displayPatterns.length === 0 && <div className="flex items-center justify-center bg-white border border-slate-200 rounded-2xl p-10 shadow-sm" data-id="element-259" data-mp="5a5yym4l7kty">
                  <span className="text-2xl font-medium text-slate-500" data-id="element-260" data-mp="e902kwgc5bh0">
                    No concerning patterns detected
                  </span>
                </div>}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-slate-200 flex items-center justify-between relative z-10" data-id="element-261" data-mp="3c2bbx3mg5iyv">
          <div className="flex items-center gap-6 text-2xl font-medium text-slate-500" data-id="element-262" data-mp="3gzbcx04fvvj4">
            <span data-id="element-263" data-mp="3ltxtdhpdoea2">{messageCount} Messages Analyzed</span>
            <span className="text-slate-300" data-id="element-264" data-mp="3qpwbink45021">·</span>
            <span data-id="element-265" data-mp="3qtab5o1e6v5s">{today}</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-266" data-mp="3vozi4cfqrec4">
            <span className="text-3xl" data-id="element-267" data-mp="3vrvcjqsdrkvt">🚩</span>
            <span className="text-2xl font-bold text-slate-900 tracking-wide" data-id="element-268" data-mp="3naz1gvs2gvq">
              toxicdetector.ai
            </span>
          </div>
        </footer>
      </div>;
  }
  return <div className="w-[1080px] h-[1080px] bg-[#0A0A0A] flex flex-col font-sans text-white relative overflow-hidden p-16" data-id="element-269" data-mp="25eeag8dhrzhc">
      <BackgroundGrid data-id="element-270" data-mp="2ad7rubvwgams" />

      {/* Subtle glow behind score */}
      <div className="absolute top-[30%] left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[150px] opacity-15 pointer-events-none" style={{
      backgroundColor: theme.color
    }} data-id="element-271" data-mp="22tookjz89kmr" />

      {/* Header */}
      <header className="flex items-center justify-between mb-12 relative z-10" data-id="element-272" data-mp="1t1anltfz4i1n">
        <div className="flex items-center gap-4" data-id="element-273" data-mp="1o5vbp71j0w7v">
          <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center text-black font-black text-2xl tracking-tighter" data-id="element-274" data-mp="2lrsn6khho1ab">
            TD
          </div>
          <h1 className="text-4xl font-black tracking-tight text-white" data-id="element-275" data-mp="2gqv5e90e599c">
            ToxicDetector.ai
          </h1>
        </div>
        <div className="text-right" data-id="element-276" data-mp="27sjhwfutmrl4">
          <div className="text-white/50 font-mono text-lg uppercase tracking-widest mb-1" data-id="element-277" data-mp="27rhx3jo06qq7">
            Date
          </div>
          <div className="text-white font-medium text-xl" data-id="element-278" data-mp="10cliljbsrskm">{today}</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col relative z-10" data-id="element-279" data-mp="2vhuqf7507chq">
        <div className="flex items-center justify-between mb-16" data-id="element-280" data-mp="2vgjjwzk6bqmx">
          <div className="flex flex-col" data-id="element-281" data-mp="30h7ic6aqf9r6">
            <h2 className="text-3xl font-mono text-white/50 uppercase tracking-widest mb-4" data-id="element-282" data-mp="30eusz82n9x43">
              Forensic Analysis
            </h2>
            <span className="text-7xl font-black tracking-tight uppercase" style={{
            color: theme.color
          }} data-id="element-283" data-mp="2rfrb27f7rnr6">
              {theme.verdict}
            </span>
            <div className="mt-4 flex items-center gap-4" data-id="element-284" data-mp="3j65jho4gk7vg">
              <span className="text-2xl text-white/70 font-medium" data-id="element-285" data-mp="35gadmz0wu1ly">
                Risk Level:
              </span>
              <div className={`px-4 py-1.5 rounded-full border ${theme.borderClass} ${theme.bgClass} inline-block`} data-id="element-286" data-mp="3aebz1qlt6d0l">
                <span className={`text-lg font-bold ${theme.textClass} uppercase tracking-widest`} data-id="element-287" data-mp="1x8r35f4qrlv">
                  {theme.label}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end" data-id="element-288" data-mp="c3s73bnh7i84">
            <span className="text-[180px] font-black leading-none tracking-tighter" style={{
            color: theme.color,
            textShadow: `0 0 40px ${theme.glowColor}`
          }} data-id="element-289" data-mp="c15zjc0l52pw">
              {score}
            </span>
            <span className="text-3xl font-mono text-white/50 uppercase tracking-widest mt-2" data-id="element-290" data-mp="3tbvv301bil2q">
              Toxicity Score
            </span>
          </div>
        </div>

        {/* Patterns Box */}
        <div className="w-full flex-1 flex flex-col" data-id="element-291" data-mp="75gwuzt9r5pd">
          <h3 className="text-2xl font-mono text-white/50 uppercase tracking-widest mb-6" data-id="element-292" data-mp="24jfgpg32fox">
            Detected Patterns ({topPatterns.length})
          </h3>

          <div className="grid grid-cols-1 gap-4" data-id="element-293" data-mp="1ilf89abx63tr">
            {displayPatterns.map((pattern, index) => {
            const info = getPatternInfo(pattern);
            const sevColor = getSeverityColor(info.severity);
            return <div key={index} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl p-6 relative overflow-hidden" data-id="element-294" data-mp="2xlk1bkt3ok2g" data-mpi={index}>
                  <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{
                backgroundColor: sevColor
              }} data-id="element-295" data-mp="2om6s8t8w1xi9" />
                  <span className="text-3xl font-bold text-white capitalize pl-4" data-id="element-296" data-mp="3hacejkflbuk7">
                    {info.type}
                  </span>
                  <span className="px-5 py-2 rounded-full text-lg font-mono font-bold uppercase tracking-widest" style={{
                '--sev-color': sevColor,
                '--sev-color-20': `${sevColor}20`,
                '--sev-color-40': `${sevColor}40`,
                backgroundColor: 'var(--sev-color-20)',
                color: 'var(--sev-color)',
                border: '1px solid var(--sev-color-40)'
              } as React.CSSProperties} data-id="element-297" data-mp="37k0yizimtuwy">
                    {info.severity}
                  </span>
                </div>;
          })}

            {displayPatterns.length === 0 && <div className="flex items-center justify-center bg-white/5 border border-white/10 rounded-2xl p-10" data-id="element-298" data-mp="ub6z6a7hr1nc">
                <span className="text-2xl font-medium text-white/50" data-id="element-299" data-mp="1434cpnlz25p8">
                  No concerning patterns detected
                </span>
              </div>}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 pt-8 border-t border-white/10 flex items-center justify-between relative z-10" data-id="element-300" data-mp="1d4u44tfut4n0">
        <div className="flex items-center gap-6 text-2xl font-mono text-white/50" data-id="element-301" data-mp="1i3nisp6vn1or">
          <span data-id="element-302" data-mp="3e58pa0xdyiqo">{messageCount} Messages Analyzed</span>
        </div>
        <div className="flex items-center gap-3" data-id="element-303" data-mp="3a4f77o6mjy5p">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" data-id="element-304" data-mp="3a5qalmw5r3wa" />
          <span className="text-2xl font-bold text-white tracking-wide" data-id="element-305" data-mp="3nw3e9d20nwft">
            toxicdetector.ai
          </span>
        </div>
      </footer>
    </div>;
}